import icons from '../../../constants/icons';
import { scopes as tableScopes } from '../../../constants/scopes';
import type { ScopeChip } from '../types';

type Translate = (key: string) => string;

export function resolveInheritedScopeName(
  scopeKey: string,
  scopeId: string,
  resolvedInheritedNames: Record<string, string>,
  t: Translate
): string {
  const cached = resolvedInheritedNames[`${scopeKey}:${scopeId}`];
  if (cached) return cached;

  switch (scopeKey) {
    case tableScopes.USER:
      return t('User context');
    case tableScopes.COMPANY:
      return t('Company context');
    case tableScopes.GAME:
      return t('Game context');
    case tableScopes.SKU:
      return t('SKU context');
    case tableScopes.KEYPROVIDER:
      return t('Key provider context');
    case tableScopes.PLATFORM:
      return t('Platform context');
    case tableScopes.KEYWORKFLOW:
      return t('Workflow context');
    default:
      return t('Context');
  }
}

export function buildInheritedScopeChips(
  effectiveScopes: Record<string, string>,
  drillScopes: Record<string, string>,
  resolveName: (scopeKey: string, scopeId: string) => string
): ScopeChip[] {
  const inheritedScopeKeys = Object.keys(effectiveScopes ?? {}).filter(
    (scopeKey) => !drillScopes?.[scopeKey]
  );

  return inheritedScopeKeys
    .map((scopeKey): ScopeChip | undefined => {
      const scopeId = effectiveScopes[scopeKey] ?? '';

      switch (scopeKey) {
        case tableScopes.USER:
          return {
            scopeKey,
            id: scopeId,
            name: resolveName(scopeKey, scopeId),
            icon: icons.USER,
            removable: false,
          };
        case tableScopes.COMPANY:
          return {
            scopeKey,
            id: scopeId,
            name: resolveName(scopeKey, scopeId),
            icon: icons.COMPANY,
            removable: false,
          };
        case tableScopes.GAME:
          return {
            scopeKey,
            id: scopeId,
            name: resolveName(scopeKey, scopeId),
            icon: icons.GAME,
            removable: false,
          };
        case tableScopes.SKU:
          return {
            scopeKey,
            id: scopeId,
            name: resolveName(scopeKey, scopeId),
            icon: icons.SKU,
            removable: false,
          };
        case tableScopes.PLATFORM:
          return {
            scopeKey,
            id: scopeId,
            name: resolveName(scopeKey, scopeId),
            icon: icons.PLATFORM,
            removable: false,
          };
        case tableScopes.KEYPROVIDER:
          return {
            scopeKey,
            id: scopeId,
            name: resolveName(scopeKey, scopeId),
            icon: icons.KEY_PROVIDER,
            removable: false,
          };
        case tableScopes.KEYWORKFLOW:
          return {
            scopeKey,
            id: scopeId,
            name: resolveName(scopeKey, scopeId),
            icon: icons.WORKFLOW,
            removable: false,
          };
        default:
          return undefined;
      }
    })
    .filter((chip): chip is ScopeChip => Boolean(chip));
}
